import { customHash } from '@shared/utils/customHash.js';

import type {
  CaptureEventProperties,
  EventCategory,
  FormsCompleted,
  FormsPaid,
  FormsStarted,
} from '../analytics.types.ts';

/**
 * FIXME: This type needs to be properly typed. :(
 */
type TODO = any; // eslint-disable-line @typescript-eslint/no-explicit-any

// Converts a union of two types into an intersection
// i.e. A | B -> A & B
type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

// Flattens two union types into a single type with optional values
// i.e. FlattenUnion<{ a: number, c: number } | { b: string, c: number }> = { a?: number, b?: string, c: number }
type FlattenUnion<T> = {
  [K in keyof UnionToIntersection<T>]: K extends keyof T
    ? T[K] extends unknown[]
      ? T[K]
      : T[K] extends object
        ? FlattenUnion<T[K]>
        : T[K]
    : UnionToIntersection<T>[K] | undefined;
};

type AllCaptureEventProperties = Omit<FlattenUnion<CaptureEventProperties<EventCategory>>, '$set' | '$groups'>;
type AllSetCaptureEventProperties = NonNullable<Pick<CaptureEventProperties<EventCategory>, '$set'>['$set']>;
type AllGroupsCaptureEventProperties = NonNullable<Pick<CaptureEventProperties<EventCategory>, '$groups'>['$groups']>;

type ParserFn<T, K extends keyof T> = (value: T[K]) => Partial<Record<K | (string & {}), unknown>>;
type CreateParserMapper<T> = Partial<{
  [K in keyof T]: ParserFn<T, K>;
}>;

type AllCaptureEventPropertiesParserMap = CreateParserMapper<AllCaptureEventProperties>;
type AllSetCaptureEventPropertiesParserMap = CreateParserMapper<AllSetCaptureEventProperties>;
type AllGroupsCaptureEventPropertiesParserMap = CreateParserMapper<AllGroupsCaptureEventProperties>;

const hashParser =
  <TKey extends keyof AllCaptureEventProperties>(key: TKey) =>
  (value: AllCaptureEventProperties[NoInfer<TKey>]): ReturnType<ParserFn<AllCaptureEventProperties, NoInfer<TKey>>> =>
    value ? ({ [key]: customHash(value) } as ReturnType<ParserFn<AllCaptureEventProperties, NoInfer<TKey>>>) : {};

/**
 * Converts sex string to numeric value
 * @param sex - The sex string ('Male', 'Female', 'Other')
 * @returns Numeric sex value (0: Female, 1: Male, 2: Other) or undefined
 */
const convertSexToNumeric = (sex: string | undefined): ReturnType<ParserFn<AllSetCaptureEventProperties, 'sex'>> => {
  if (!sex) {
    return { sex: undefined };
  }

  switch (sex) {
    case 'Female':
      return { sex: 0 };
    case 'Male':
      return { sex: 1 };
    case 'Other':
      return { sex: 2 };
    default:
      return { sex: undefined };
  }
};

const formsPaidParser = (value: FormsPaid | undefined) =>
  value?.reduce((acc, item): ReturnType<ParserFn<AllSetCaptureEventProperties, 'formsPaid'>> => {
    const { healthCategory, paidAt } = item;

    const hashedHealthCategory = customHash(healthCategory);

    return {
      ...acc,
      [`paidForm_${hashedHealthCategory}`]: { healthCategory: hashedHealthCategory, paidAt },
    };
  }, {}) ?? {};

const formsCompletedParser = (value: FormsCompleted | undefined) =>
  value?.reduce((acc, item): ReturnType<ParserFn<AllSetCaptureEventProperties, 'formsCompleted'>> => {
    const { healthCategory, completedAt } = item;

    const hashedHealthCategory = customHash(healthCategory);

    return {
      ...acc,
      [`completedForm_${hashedHealthCategory}`]: { healthCategory: hashedHealthCategory, completedAt },
    };
  }, {}) ?? {};

const formsStartedParser = (value: FormsStarted | undefined) =>
  value?.reduce((acc, item): ReturnType<ParserFn<AllSetCaptureEventProperties, 'formsStarted'>> => {
    const { healthCategory, startedAt } = item;

    const hashedHealthCategory = customHash(healthCategory);

    return {
      ...acc,
      [`startedForm_${hashedHealthCategory}`]: { healthCategory: hashedHealthCategory, startedAt },
    };
  }, {}) ?? {};

type ParserMap = AllCaptureEventPropertiesParserMap &
  AllSetCaptureEventPropertiesParserMap &
  AllGroupsCaptureEventPropertiesParserMap;

const parserMap = {
  sex: convertSexToNumeric,
  formsPaid: formsPaidParser,
  formsStarted: formsStartedParser,
  formsCompleted: formsCompletedParser,
  healthCategory: hashParser('healthCategory'),
} as const satisfies ParserMap;

export const parseCaptureProperties = (properties?: CaptureEventProperties<EventCategory>) => {
  if (!properties) {
    return {};
  }

  const parsedProperties = Object.fromEntries(
    Object.entries(properties).flatMap(([key, value]) => {
      if (key in parserMap) {
        const parser = parserMap[key as keyof typeof parserMap];
        const parsedValue = parser ? parser(value) : { [key]: value };

        return Object.entries(parsedValue);
      }

      // Parse $set properties
      if (key === '$set') {
        return [
          [
            key,
            Object.fromEntries(
              Object.entries(value).flatMap(([k, v]) => {
                const parser = parserMap[k as keyof typeof parserMap];

                const parsedValue = parser ? parser(v as TODO) : { [k]: v };

                return Object.entries(parsedValue);
              }),
            ),
          ],
        ];
      }

      // Parse $set_once properties
      if (key === '$set_once') {
        return [
          [
            key,
            Object.fromEntries(
              Object.entries(value).flatMap(([k, v]) => {
                const parser = parserMap[k as keyof typeof parserMap];

                const parsedValue = parser ? parser(v as TODO) : { [k]: v };

                return Object.entries(parsedValue);
              }),
            ),
          ],
        ];
      }

      // Parse $groups properties
      if (key === '$groups') {
        return [
          [
            key,
            Object.fromEntries(
              Object.entries(value).flatMap(([k, v]) => {
                const parser = parserMap[k as keyof typeof parserMap];

                const parsedValue = parser ? parser(v as TODO) : { [k]: v };

                return Object.entries(parsedValue);
              }),
            ),
          ],
        ];
      }

      return [[key, value]];
    }),
  );

  return parsedProperties;
};
